import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  NewCalloutScreen,
  type NewCalloutInitialValues,
} from '@/screens/NewCalloutScreen'
import { DayBookingsPreview } from '@/components/DayBookingsPreview'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import { fromZaLocalToUtcIso, toZaNowDateKey } from '@/lib/datetime'
import { withSpan } from '@/lib/sentry'
import { useDb } from '@/lib/dbContext'
import { createCalloutActivityAdapter } from '@/activity/adapters'

interface NewCalloutModalContextValue {
  openNewCalloutModal: (initialDay?: string) => void
}

const NewCalloutModalContext = createContext<NewCalloutModalContextValue | null>(null)

export function useNewCalloutModal() {
  const ctx = useContext(NewCalloutModalContext)
  if (!ctx) {
    throw new Error('useNewCalloutModal must be used within NewCalloutModalProvider')
  }
  return ctx
}

const calloutSaveErrorMessage = 'Unable to save callout. Please try again.'

const calloutFieldLabels: Record<string, string> = {
  external_id: 'External ID',
  full_name: 'Full name',
  customer_id: 'Customer',
  start_time: 'Start time',
  end_time: 'End time',
  is_whole_day: 'Whole day',
  status: 'Status',
  issue_summary: 'Issue summary',
  contact_address: 'Address',
  contact_name: 'Contact name',
  contact_phone: 'Contact phone',
  contact_email: 'Contact email',
  invoice_name: 'Invoice to',
  invoice_address: 'Invoice address',
  invoice_vat: 'Invoice VAT',
  invoice_email: 'Invoice email',
  invoice_phone: 'Invoice phone',
  invoice_id: 'Invoice ID',
  external_customer_id: 'External customer ID',
}

function toTimestampsFromFormValues(
  values: NewCalloutInitialValues,
): { startTime: string; endTime: string; wholeDay: boolean } {
  const wholeDay = values.wholeDay ?? false
  const day = values.day
  const startTimePart = values.time && !wholeDay ? values.time : '09:00'
  const startTime = fromZaLocalToUtcIso(day, startTimePart)

  let endTime: string
  if (values.endTime && !wholeDay) {
    endTime = fromZaLocalToUtcIso(day, values.endTime)
  } else if (wholeDay) {
    endTime = fromZaLocalToUtcIso(day, '23:59')
  } else {
    const [hourStr, minuteStr] = startTimePart.split(':')
    const hour = Number(hourStr ?? '9')
    const endHour = String(Math.min(23, hour + 1)).padStart(2, '0')
    endTime = fromZaLocalToUtcIso(day, `${endHour}:${minuteStr ?? '00'}`)
  }

  return { startTime, endTime, wholeDay }
}

export function NewCalloutModalProvider({ children }: { children: ReactNode }) {
  const db = useDb()
  const calloutActivity = useMemo(() => createCalloutActivityAdapter(db), [db])
  const [isOpen, setIsOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [initialDayOverride, setInitialDayOverride] = useState<string | null>(null)

  const openNewCalloutModal = useCallback((initialDay?: string) => {
    setErrorMessage(null)
    setInitialDayOverride(initialDay ?? null)
    setIsOpen(true)
  }, [])

  const handleSave = useCallback(
    async (values: NewCalloutInitialValues) => {
      setErrorMessage(null)
      try {
        await withSpan(
          {
            op: 'callout.create',
            name: 'Create callout from global modal',
          },
          async () => {
            const { startTime, endTime, wholeDay } = toTimestampsFromFormValues(values)

            let customerId = values.customerId

            if (!customerId && values.customerName.trim().length > 0) {
              const customer = await withSpan(
                {
                  op: 'customer.create',
                  name: 'Create customer during callout booking',
                },
                () =>
                  db.createCustomerAndWait({
                    fullName: values.customerName,
                    externalId: '',
                    contactAddress: values.address,
                    contactEmail: values.email,
                    contactPhone: values.phone,
                    invoiceName: values.invoiceTo || values.customerName,
                    invoiceAddress: values.invoiceAddress ?? values.address,
                    invoiceEmail: values.invoiceEmail ?? values.email,
                    invoicePhone: values.phone,
                    invoiceVat: values.vat ?? '',
                  }),
              )
              customerId = customer.id
            }

            const callout = await db.createCalloutAndWait({
              customerId: customerId ?? '',
              startTime,
              endTime,
              isWholeDay: wholeDay,
              status: 'Booked',
              issueSummary: values.issueSummary,
              contactAddress: values.address,
              contactName: values.customerName,
              contactPhone: values.phone,
              contactEmail: values.email,
              invoiceName: values.invoiceTo || values.customerName,
              invoiceVat: values.vat ?? '',
              invoiceAddress: values.invoiceAddress ?? values.address,
              invoiceEmail: values.invoiceEmail ?? values.email,
              invoicePhone: values.phone,
              invoiceId: undefined,
              billablesConsumed: [],
              auditLogs: [],
              statusChanges: [],
            })

            if (values.notes && values.notes.trim().length > 0) {
              calloutActivity.appendLog(callout.id, {
                text: values.notes,
              })
            }
          },
        )

        setIsOpen(false)
        setInitialDayOverride(null)
      } catch (error) {
        setErrorMessage(
          formatPocketBaseError(error, {
            fallbackMessage: calloutSaveErrorMessage,
            fieldLabels: calloutFieldLabels,
          }),
        )
      }
    },
    [db],
  )

  const initialValues: NewCalloutInitialValues = {
    day: initialDayOverride ?? toZaNowDateKey(),
    wholeDay: false,
    customerName: '',
    address: '',
    phone: '',
    email: '',
    invoiceTo: '',
    issueSummary: '',
  }

  const value = useMemo(
    () => ({ openNewCalloutModal }),
    [openNewCalloutModal],
  )

  return (
    <NewCalloutModalContext.Provider value={value}>
      {children}

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            const shouldClose = window.confirm(
              'Discard this draft callout? Unsaved changes will be lost.',
            )
            if (!shouldClose) return
            setErrorMessage(null)
            setInitialDayOverride(null)
          }

          setIsOpen(open)
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>New callout</DialogTitle>
            <DialogDescription>
              Book a technician visit to a customer.
            </DialogDescription>
          </DialogHeader>
          <NewCalloutScreen
            showHeader={false}
            mode="create"
            initialValues={initialValues}
            errorMessage={errorMessage}
            renderDayBookings={(booking) => <DayBookingsPreview {...booking} />}
            onSave={handleSave}
            onCancel={() => {
              setIsOpen(false)
              setErrorMessage(null)
              setInitialDayOverride(null)
            }}
          />
        </DialogContent>
      </Dialog>
    </NewCalloutModalContext.Provider>
  )
}
